import { match } from 'ts-pattern';

import type { CurrencyCode } from '@fragment/types/currencyCodes';
import { currencyPrecision as currencyPrecisionMap } from '@fragment/types/currencyCodes';
import type { InvoiceRowItem } from '@fragment/ui/src/components/Payments/InvoiceItemTable';

export type PendingAdd = {
  type: 'add';
  tempId: string;
  data: InvoiceRowItem;
};

export type PendingRemove = {
  type: 'remove';
  lineItemId: string;
};

export type PendingUpdate = {
  type: 'update';
  lineItemId: string;
  amount: string;
};

export type PendingChange = PendingAdd | PendingRemove | PendingUpdate;

export type PendingChangeOperation =
  | { op: 'add'; tempId: string; data: InvoiceRowItem }
  | { op: 'remove'; id: string }
  | {
      op: 'update';
      id: string;
      field: string;
      value: string | number;
    };

const isPendingAddForId = (changes: PendingChange[], id: string): boolean =>
  changes.some((c) => c.type === 'add' && c.tempId === id);

const updatePendingAddData = (
  changes: PendingChange[],
  id: string,
  field: string,
  value: string | number
): PendingChange[] =>
  changes.map((c) => {
    if (c.type !== 'add' || c.tempId !== id) return c;

    const itemData = match(field)
      .with('amount', () => ({ ...c.data, amount: Number(value) }))
      .with('currency', () => ({
        ...c.data,
        currency: value as CurrencyCode,
        currencyPrecision: currencyPrecisionMap[value as CurrencyCode],
      }))
      .with('description', () => ({ ...c.data, description: value as string }))
      .with('payoutUser', () => ({ ...c.data, payoutUser: value as string }))
      .with('productCode', () => ({ ...c.data, productCode: value as string }))
      .otherwise(() => c.data);

    return { ...c, data: itemData };
  });

/**
 * Pure function that computes the next pending changes state given an operation.
 * This handles the logic for:
 * - Adding new items
 * - Removing items (and cleaning up any related updates)
 * - Updating items (either pending adds or server items)
 */
export const applyPendingChangeOperation = (
  currentChanges: PendingChange[],
  operation: PendingChangeOperation
): PendingChange[] =>
  match(operation)
    .with({ op: 'add' }, ({ tempId, data }) => [
      ...currentChanges,
      { type: 'add' as const, tempId, data },
    ])
    .with({ op: 'remove' }, ({ id }) => {
      if (isPendingAddForId(currentChanges, id)) {
        // If it's a pending add, just remove it from the list
        return currentChanges.filter(
          (c) => !(c.type === 'add' && c.tempId === id)
        );
      }
      // For server items, filter out any existing updates and add the remove
      const filtered = currentChanges.filter(
        (c) => !(c.type === 'update' && c.lineItemId === id)
      );
      return [...filtered, { type: 'remove' as const, lineItemId: id }];
    })
    .with({ op: 'update' }, ({ id, field, value }) => {
      if (isPendingAddForId(currentChanges, id)) {
        // Update the pending add's data
        return updatePendingAddData(currentChanges, id, field, value);
      }
      // Server line item: API only supports updating amount
      if (field === 'amount') {
        const rest = currentChanges.filter(
          (c) => !(c.type === 'update' && c.lineItemId === id)
        );
        return [
          ...rest,
          { type: 'update' as const, lineItemId: id, amount: String(value) },
        ];
      }
      // For server items, other fields can't be updated
      return currentChanges;
    })
    .exhaustive();
